import React, { useState, useEffect } from 'react'

const debugMode = false

const initialState = {
  q1: '',
  q2: '',
  q3: '',
  q4: '',
  q5: '',
  q6: '',
  q7: '',
  q8: '',
  q9: '',
  q10: '',
}

const q1Options = ['Yes', 'No']
const q2Options = ['Yes', 'No']
const q3Options = ['Yes', 'No']
const q4Options = ['Yes', 'No']
const q5Options = ['Yes', 'No']
const q6Options = ['Yes', 'No']
const q7Options = ['Yes', 'No']
const q8Options = ['Yes', 'No']
const q9Options = ['Yes', 'No']
const q10Options = ['Yes', 'No', 'Not currently pregnant']

const debugPointLabels = {
  q1: { Yes: '+3', No: '+0' },
  q2: { Yes: '+3', No: '+0' },
  q3: { Yes: '+3', No: '+0' },
  q4: { Yes: '+2', No: '+0' },
  q5: { Yes: '+2', No: '+0' },
  q6: { Yes: '+2', No: '+0' },
  q7: { Yes: '+2', No: '+0' },
  q8: { Yes: '+2', No: '+0' },
  q9: { Yes: '+0', No: '+1' },
  q10: { Yes: '+0', No: '+1', 'Not currently pregnant': '+0' },
}

export default function CARPREG2Patients() {
  const [form, setForm] = useState(initialState)
  const [showResults, setShowResults] = useState(false)
  const [urlScore, setUrlScore] = useState(null)
  const [validationError, setValidationError] = useState('')

  // Parse URL parameters on component mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const scoreParam = urlParams.get('score')
    if (scoreParam && !isNaN(scoreParam)) {
      const parsedScore = parseInt(scoreParam)
      setUrlScore(parsedScore)
      setShowResults(true)
    }
  }, [])

  // Get risk percentage based on score
  const getEstimatedRisk = (score) => {
    if (score === 1) return 'The patient’s risk of having a heart problem during pregnancy is low, with a less than 5% chance of a cardiac event (maternal cardiac death; cardiac arrest; sustained arrhythmia requiring treatment; left-sided heart failure (HF) defined as pulmonary edema; right-sided HF; stroke or transient ischemic attack; cardiac thromboembolism; myocardial infarction; and vascular dissection).'
    if (score === 2) return 'The patient’s risk of having a heart problem during pregnancy is in the intermediate range, with an approximately 10% chance of having a cardiac event (maternal cardiac death; cardiac arrest; sustained arrhythmia requiring treatment; left-sided heart failure (HF) defined as pulmonary edema; right-sided HF; stroke or transient ischemic attack; cardiac thromboembolism; myocardial infarction; and vascular dissection).'
    if (score === 3) return 'The patient’s risk of having a heart problem during pregnancy is in the intermediate range, with an approximately 15% chance of having a cardiac event (maternal cardiac death; cardiac arrest; sustained arrhythmia requiring treatment; left-sided heart failure (HF) defined as pulmonary edema; right-sided HF; stroke or transient ischemic attack; cardiac thromboembolism; myocardial infarction; and vascular dissection).'
    if (score === 4) return 'The patient’s risk of having a heart problem during pregnancy is in the intermediate to high range, with an approximately 22% chance of having a cardiac event (maternal cardiac death; cardiac arrest; sustained arrhythmia requiring treatment; left-sided heart failure (HF) defined as pulmonary edema; right-sided HF; stroke or transient ischemic attack; cardiac thromboembolism; myocardial infarction; and vascular dissection).'
    if (score >= 5) return 'The patient’s risk of having a heart problem during pregnancy is high, with an approximately 41% chance of having a cardiac event (maternal cardiac death; cardiac arrest; sustained arrhythmia requiring treatment; left-sided heart failure (HF) defined as pulmonary edema; right-sided HF; stroke or transient ischemic attack; cardiac thromboembolism; myocardial infarction; and vascular dissection).'
    return 'Unknown'
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setForm((prev) => ({ ...prev, [name]: value }))
  }

  // Scoring logic
  const calculateScore = (form) => {
    let total = 0

    // Q1: Yes=3, No=0
    total += form.q1 === 'Yes' ? 3 : 0

    // Q2: Yes=3, No=0
    total += form.q2 === 'Yes' ? 3 : 0

    // Q3: Yes=3, No=0
    total += form.q3 === 'Yes' ? 3 : 0

    // Q4: Yes=2, No=0
    total += form.q4 === 'Yes' ? 2 : 0

    // Q5: Yes=2, No=0
    total += form.q5 === 'Yes' ? 2 : 0

    // Q6: Yes=2, No=0
    total += form.q6 === 'Yes' ? 2 : 0

    // Q7: Yes=2, No=0
    total += form.q7 === 'Yes' ? 2 : 0

    // Q8: Yes=2, No=0
    total += form.q8 === 'Yes' ? 2 : 0

    // Q9: Yes=0, No=1
    total += form.q9 === 'No' ? 1 : 0

    // Q10: Yes=0, No=1
    total += form.q10 === 'No' ? 1 : 0

    return total
  }

  const runningScore = calculateScore(form)

  const getOptionPointLabel = (questionName, option) => {
    return debugPointLabels[questionName]?.[option] ?? '+0'
  }

  const renderOptions = (questionName, options) => {
    return options.map((opt) => (
      <label key={opt} className="radio-option">
        <input
          type="radio"
          name={questionName}
          value={opt}
          checked={form[questionName] === opt}
          onChange={handleChange}
        />
        <span className="radio-option-text">{opt}</span>
        {debugMode && (
          <span className="radio-option-points">{getOptionPointLabel(questionName, opt)}</span>
        )}
      </label>
    ))
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    
    // Clear any previous validation errors
    setValidationError('')
    
    // Validate all fields are completed
    const missingFields = []
    for (let i = 1; i <= 10; i++) {
      if (!form[`q${i}`]) {
        missingFields.push(`Question ${i}`)
      }
    }
    
    if (missingFields.length > 0) {
      setValidationError(`Please complete all questions before submitting. Missing: ${missingFields.join(', ')}`)
      return
    }
    
    // Redirect to results page with score parameter
    window.location.href = `${window.location.pathname}?score=${runningScore}`
  }

  const handleStartOver = () => {
    // Redirect to page URL without query parameters
    window.location.href = window.location.pathname
  }

  // Show results if URL parameter is present
  if (showResults && urlScore !== null) {
    return (
      <div className="carpreg2-results">
        <div>
          <h2>CARPREG II Score: {urlScore}</h2>

          <h5>Estimated Risk</h5>
          <div className="estimated-risk">
            {getEstimatedRisk(urlScore)}
          </div>
          <p className="results-disclaimer">
            This prediction is an estimate only.
          </p>
        </div>
        <button 
          className="btn arrow-link red"
          type="button" 
          onClick={handleStartOver}
        >
          <span className="title" data-title="Start Over">
            <span className="title-text">Start Over</span>
            <span className="title-bold-spacer">Start Over</span>
          </span>
          <svg className="link-arrow" width="60.9075413" height="23" viewBox="0 0 60.9075413 23" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle className="circle" cx="35" cy="11" r="10.5" stroke="#EA0029"/>
            <line className="line" x1="0" y1="10.9453" x2="39" y2="10.9453" stroke="#EA0029"/>
            <path className="arrow" transform-origin="100% 50%" stroke="#EA0029" d="M34,16.9452991l5-5.999999-5-5.99999"/>
            <path className="heart" opacity="0" stroke="#EA0029" d="M58.7248557,11.1299175c.8732012-1.0419667,1.3983597-2.3794889,1.3983597-3.83914.0007368-3.322673-2.7200319-6.0156097-6.075484-6.0156097-2.0735386,0-3.904295,1.028834-5.0006808,2.5990389-1.0971225-1.5702049-2.9279353-2.5990389-5.0014175-2.5990389-3.3561325,0-6.0762207,2.6929366-6.0762207,6.0156097,0,1.4596511.5252148,2.7971733,1.398416,3.83914.1094622.1304803.2245475.2568141.3445193.3776949l9.3339662,10.2172197,9.3339662-10.2172197c.1199696-.1208808.235055-.2465321.3445757-.3776949Z"/>
          </svg>
        </button>
      </div>
    )
  }

  return (
    <div className="carpreg2-form">
      {debugMode && (
        <div className="carpreg2-debug-score" aria-live="polite">
          <span className="carpreg2-debug-score-label">Running Total</span>
          <span className="carpreg2-debug-score-value">{runningScore}</span>
        </div>
      )}
      <form onSubmit={handleSubmit}>
      <fieldset>
        <legend>1. Does the patient have a history of cardiac events or arrhythmias?</legend>
        {renderOptions('q1', q1Options)}
      </fieldset>

      <fieldset>
        <legend>2. Does the patient have baseline NYHA Class III–IV symptoms or cyanosis?</legend>
        {renderOptions('q2', q2Options)}
        <div className="footnote">Cyanosis: saturation &lt;90%</div>
      </fieldset>

      <fieldset>
        <legend>3. Does the patient have a mechanical heart valve?</legend>
        {renderOptions('q3', q3Options)}
      </fieldset>

      <fieldset>
        <legend>4. Does the patient have ventricular dysfunction (LVEF &lt;55%)?</legend>
        {renderOptions('q4', q4Options)}
      </fieldset>

      <fieldset>
        <legend>5. Does the patient have high-risk left-sided valvular disease or left ventricular outflow tract obstruction?</legend>
        {renderOptions('q5', q5Options)}
        <div className="footnote">High risk left sided heart disease/LVOT obstruction: aortic valve area &lt;1.5 cm<sup>2</sup>, subaortic gradient &gt;30 mm Hg, mitral valve area &lt;2cm<sup>2</sup>, or moderate to severe mitral regurgitation</div>
      </fieldset>

      <fieldset>
        <legend>6. Does the patient have pulmonary hypertension?</legend>
        {renderOptions('q6', q6Options)}
        <div className="footnote">Pulmonary hypertension: right ventricular systolic pressure &gt; 50 mm Hg in the absence of right ventricular outflow tract obstruction</div>
      </fieldset>

      <fieldset>
        <legend>7. Does the patient have coronary artery disease?</legend>
        {renderOptions('q7', q7Options)}
        <div className="footnote">Coronary artery disease: angiographically proven coronary obstruction or past myocardial infarction</div>
      </fieldset>

      <fieldset>
        <legend>8. Does the patient have a high-risk aortopathy?</legend>
        {renderOptions('q8', q8Options)}
        <div className="footnote">High risk aortopathy: Marfan syndrome, bicuspid aortic valve with aortic dimension &gt;45 mm, Loeys-Dietz syndrome, vascular Ehlers Danlos syndrome, or prior aortic dissection or pseudoanuerysm</div>
      </fieldset>

      <fieldset>
        <legend>9. Has the patient had a prior cardiac intervention?</legend>
        {renderOptions('q9', q9Options)}
        <div className="footnote">Prior cardiac interventions: previous cardiac repair of congenital lesions, valvular replacements or repairs, or percutaneous or operative treatment of arrhythmias.</div>
      </fieldset>

      <fieldset>
        <legend>10. Does the patient have a late pregnancy assessment?</legend>
        {renderOptions('q10', q10Options)}
        <div className="footnote">A late pregnancy assessment is an assessment after 20 weeks GA</div>
      </fieldset>

      <button className="btn arrow-link red" type="submit">
        <span className="title" data-title="Submit">
          <span className="title-text">See the patient’s risk</span>
          <span className="title-bold-spacer">See the patient’s risk</span>
        </span>
        <svg className="link-arrow" width="60.9075413" height="23" viewBox="0 0 60.9075413 23" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle className="circle" cx="35" cy="11" r="10.5" stroke="#EA0029"/>
          <line className="line" x1="0" y1="10.9453" x2="39" y2="10.9453" stroke="#EA0029"/>
          <path className="arrow" transform-origin="100% 50%" stroke="#EA0029" d="M34,16.9452991l5-5.999999-5-5.99999"/>
          <path className="heart" opacity="0" stroke="#EA0029" d="M58.7248557,11.1299175c.8732012-1.0419667,1.3983597-2.3794889,1.3983597-3.83914.0007368-3.322673-2.7200319-6.0156097-6.075484-6.0156097-2.0735386,0-3.904295,1.028834-5.0006808,2.5990389-1.0971225-1.5702049-2.9279353-2.5990389-5.0014175-2.5990389-3.3561325,0-6.0762207,2.6929366-6.0762207,6.0156097,0,1.4596511.5252148,2.7971733,1.398416,3.83914.1094622.1304803.2245475.2568141.3445193.3776949l9.3339662,10.2172197,9.3339662-10.2172197c.1199696-.1208808.235055-.2465321.3445757-.3776949Z"/>
        </svg>
      </button>

      {validationError && (
        <div style={{ marginTop: '1em', padding: '10px', backgroundColor: '#ffe6e6', border: '1px solid #ff6b6b', borderRadius: '4px', color: '#d63031' }}>
          {validationError}
        </div>
      )}
    </form>
    </div>
  )
}